/**
 * processedJobs
 * =============
 * Set globale singleton di `job.id` già processati da QUALSIASI tab di QUESTO browser
 * dello stesso PC. Condiviso tra tab tramite `BroadcastChannel` + `localStorage`.
 *
 * ⚠️ SCOPO: condiviso tra `fastPrint.js` (stampa diretta sincrona dal click) e
 * `BrowserPrintAgent.jsx` (stampa via WebSocket broadcast) E tra TAB diversi del
 * browser per evitare di stampare lo STESSO job due volte sullo stesso PC.
 *
 * Scenari risolti:
 *  1. Stesso tab: fast-path stampa + WS broadcast arriva → skip
 *  2. Tab diversi sullo stesso PC: il tab A stampa via WS broadcast, il tab B
 *     riceve lo stesso broadcast → skip
 *
 * Cleanup automatico: ogni id viene rimosso dopo 90s.
 */
const processedJobs = new Set();
const STORAGE_KEY = "smarttable.processedJobs.v1";
const LOCK_KEY_PREFIX = "smarttable.printLock.";

// BroadcastChannel se supportato (Chrome/Edge/Firefox moderni), fallback su storage event
let bc = null;
try {
    if (typeof BroadcastChannel !== "undefined") {
        bc = new BroadcastChannel("smarttable-print-jobs");
        bc.onmessage = (e) => {
            if (e?.data?.jobId) {
                processedJobs.add(e.data.jobId);
            }
        };
    }
} catch (_e) { /* */ }

// Fallback cross-tab via storage event (anche con BC attivo, è inoffensivo)
if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
        if (e.key === STORAGE_KEY && e.newValue) {
            try {
                const { jobId } = JSON.parse(e.newValue) || {};
                if (jobId) processedJobs.add(jobId);
            } catch (_e) { /* */ }
        }
    });
}

/**
 * Registra un job come "in elaborazione" o "completato".
 * Propagato a tutti i tab dello stesso browser sullo stesso PC.
 * Auto-cleanup dopo 90s.
 */
export function markJobProcessed(jobId) {
    if (!jobId) return;
    processedJobs.add(jobId);
    // Propaga a altri tab via BroadcastChannel
    try { bc?.postMessage({ jobId, t: Date.now() }); } catch (_e) { /* */ }
    // Fallback via storage event (i tab di altri PC IGNORANO questo, è solo per same-origin)
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ jobId, t: Date.now() }));
    } catch (_e) { /* */ }
    setTimeout(() => processedJobs.delete(jobId), 90000);
}

/**
 * Controlla se un job è già stato preso in carico da fastPrint o BrowserPrintAgent.
 */
export function isJobProcessed(jobId) {
    return !!jobId && processedJobs.has(jobId);
}

/**
 * Lock atomico cross-tab via localStorage per la stampa di un singolo job.
 * Ritorna `true` se questo tab ottiene il lock (deve stampare), `false` se
 * un altro tab dello stesso PC ha già preso il lock entro 90 secondi.
 *
 * Garantisce che, anche con BroadcastChannel non perfettamente sincrono,
 * solo UN tab tenta effettivamente la stampa USB del job.
 */
export function tryAcquirePrintLock(jobId) {
    if (!jobId) return false;
    const key = LOCK_KEY_PREFIX + jobId;
    try {
        const existing = localStorage.getItem(key);
        if (existing) {
            const ts = parseInt(existing, 10);
            if (!isNaN(ts) && (Date.now() - ts) < 90000) {
                return false; // Lock attivo da meno di 90s → altro tab sta stampando
            }
        }
        localStorage.setItem(key, String(Date.now()));
        // Cleanup automatico
        setTimeout(() => {
            try { localStorage.removeItem(key); } catch (_e) { /* */ }
        }, 90000);
        return true;
    } catch (_e) {
        return true; // Se localStorage non disponibile, default ottimistico
    }
}
